import React from 'react';
import { CheckCircle2, Mail, CalendarDays, Rocket } from 'lucide-react';
import { Button } from '@/components/ui/button';

const PURPLE = '#7C3AED';
const BG_DARK = '#0A0E17';
const CARD_BG = '#121829';
const BORDER = 'rgba(255,255,255,0.08)';

// ---------------------------------------------------------------------------
// OnboardingSuccessPage — confirmation surface after signup checkout. Sits on
// the same dark marketing-style backdrop as SignupPage and tells the new
// user where to look next (email + dashboard).
// ---------------------------------------------------------------------------

export default function OnboardingSuccessPage() {
  return (
    <div
      className="flex min-h-screen items-center justify-center px-4 py-10"
      style={{ backgroundColor: BG_DARK }}
    >
      <div className="w-full max-w-[520px]">
        <div className="mb-8 text-center">
          <p
            className="font-sans text-3xl font-bold tracking-tight"
            style={{ color: PURPLE }}
          >
            Cadence
          </p>
          <p className="mt-1 text-sm text-white/60">Welcome to Cadence</p>
        </div>

        {/* Success card */}
        <div
          className="mb-6 rounded-2xl border p-6 text-center md:p-8"
          style={{ backgroundColor: CARD_BG, borderColor: BORDER }}
        >
          <CheckCircle2 className="mx-auto mb-3 h-16 w-16 text-emerald-400" />
          <p className="mb-2 text-xl font-semibold text-white">
            Your account has been created
          </p>
          <p className="text-sm text-white/60">
            Check your email to set your password and get started.
          </p>
        </div>

        {/* What happens next */}
        <div
          className="mb-6 rounded-2xl border p-6 md:p-8"
          style={{ backgroundColor: CARD_BG, borderColor: BORDER }}
        >
          <p className="mb-6 text-lg font-semibold text-white">What happens next</p>
          <NextStep
            icon={Mail}
            title="Your 14-day trial has started"
            detail="Full access to all features, no restrictions during your trial."
          />
          <NextStep
            icon={CalendarDays}
            title="We'll send you a daily planning guide by email"
            detail="Tips and best practices to get the most from your operating rhythm."
          />
          <NextStep
            icon={Rocket}
            title='Start by clicking "Start My Day" on your dashboard'
            detail="Your AI-powered daily plan will be generated in seconds."
            isLast
          />
        </div>

        <Button
          onClick={() => { window.location.href = '/'; }}
          size="lg"
          className="h-12 w-full text-base font-semibold text-white hover:opacity-90"
          style={{ backgroundColor: PURPLE }}
        >
          Go to login
        </Button>

        <p className="mt-6 text-center text-[13px] text-white/60">
          If you don't receive an email within a few minutes, check your spam folder.
        </p>
      </div>
    </div>
  );
}

function NextStep({
  icon: Icon,
  title,
  detail,
  isLast,
}: {
  icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  title: string;
  detail: string;
  isLast?: boolean;
}) {
  return (
    <div className={`flex items-start gap-3 ${isLast ? '' : 'mb-5'}`}>
      <Icon className="mt-0.5 h-5 w-5 shrink-0" style={{ color: PURPLE }} />
      <div>
        <p className="text-[15px] font-medium text-white">{title}</p>
        <p className="text-[13px] text-white/60">{detail}</p>
      </div>
    </div>
  );
}
